import { useState, useEffect, useContext, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";

import Avatar from "@mui/material/Avatar";

import DataTable from "examples/Tables/DataTable";

import qs from "query-string";
import api from "services/api";
import toast from "react-hot-toast";
import moment from "moment";

import SharedContentContext from "context/shared-content";
import { AccountContext } from "context/account";

import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import { Empty } from "antd";

import { compare } from "utils/helper";
import Tooltip from "@mui/material/Tooltip";

const avatarSize = 26;

const SharedMoodboardList = ({ size = 60, limit, tableOptions = {} }) => {
  const [moodboards, setMoodboards] = useState([]);
  const [filteredMoodboards, setFilteredMoodboards] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState({});

  const location = useLocation();
  const url = location.href ? location.href : location.pathname + location.search;
  const urlObj = qs.parseUrl(url);

  const { resourceList } = useContext(SharedContentContext);
  const account = useContext(AccountContext);

  const navigate = useNavigate();
  const owners = moodboards.filter((x) => x.owner).map((x) => x.owner);

  useEffect(() => {
    retrieveMoodboards();
  }, [url]);

  const retrieveMoodboards = async () => {
    try {
      const { data } = await api.moodboard.sharedList();
      let myMoodboard = data?.moodboards || [];
      myMoodboard = myMoodboard.sort((a, b) => compare(a, b, "-updated_at"));
      if (limit) {
        myMoodboard = myMoodboard.slice(0, limit);
      }

      setMoodboards(myMoodboard);
      setFilteredMoodboards(myMoodboard);
    } catch (err) {
      toast.error(`Retrieve moodboards for collection:${detail["title"]} failed!`);
      console.log(err);
    }
  };

  const getMoodboardRow = (item) => {
    let resource = resourceList.find((x) => x.resource_id == item.id) || {};
    const owner = item.owner || {};
    const storeInfo = owner?.stores?.[0] || {};

    return {
      key: item.id,
      image: item?.img,
      name: item.name,
      updated_at: item.updated_at,
      shared_at: resource?.created_at,
      owner: storeInfo,
      ownerName: storeInfo?.name,
    };
  };

  const columns = [
    {
      Header: "Image",
      accessor: "image",
      align: "center",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return (
          <Link to={`/shared-with-me/moodboards?id=${row?.original?.key}`}>
            <ImageCardWrapper data={value} mode={"normal"} height={size} width={size} iconMode />
          </Link>
        );
      },
    },
    {
      Header: "Name",
      accessor: "name",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return <div style={{ maxWidth: 400 }}>{value}</div>;
      },
    },
    {
      Header: "Updated Date",
      accessor: "updated_at",
      resizable: true,
      Cell: ({ row, value }) => {
        return (
          <Tooltip title={value ? moment(value).format("MM/DD/YY HH:mm:ss") : ""}>
            <div>{value ? moment(value).format("MM/DD/YY") : ""}</div>
          </Tooltip>
        );
      },
    },
    {
      Header: "Shared Date",
      accessor: "shared_at",
      Cell: ({ row, value }) => {
        return (
          <Tooltip title={value ? moment(value).format("MM/DD/YY HH:mm:ss") : ""}>
            <div>{value ? moment(value).format("MM/DD/YY") : ""}</div>
          </Tooltip>
        );
      },
    },
    {
      Header: "Owner",
      accessor: "ownerName",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return (
          <SuiBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="inherit"
            sx={{ cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              if (row.original?.owner?.id) {
                account?.id
                  ? navigate(`/wall/portfolio/${row.original?.owner?.id}`)
                  : navigate(`/public/portfolio/${row.original?.owner?.id}`);
              }
            }}
          >
            <Avatar
              src={row.original?.owner?.metadata?.avatar}
              sx={{ width: avatarSize, height: avatarSize, mr: 0.5 }}
            />
            {value}
          </SuiBox>
        );
      },
    },
  ];

  const moodboardRows = filteredMoodboards.map((item) => getMoodboardRow(item));

  const handleCollectionRowClick = (row) => {
    navigate(`/shared-with-me/moodboards?id=${row?.original?.key}`);
  };

  const handleFilterMoodboard = (owner) => {
    if (owner.id == selectedOwner.id) {
      setSelectedOwner({});
      setFilteredMoodboards(moodboards);
    } else {
      setSelectedOwner(owner);
      const newMoodboards = moodboards.filter((x) => x?.owner?.id == owner?.id);
      setFilteredMoodboards(newMoodboards);
    }
  };

  const removeDuplicate = (userList) => {
    let idSet = new Set();
    let filteredList = [];
    for (let i = 0; i < userList.length; i++) {
      if (idSet.has(userList[i].id)) {
        //
      } else {
        idSet.add(userList[i].id);
        filteredList.push(userList[i]);
      }
    }
    return filteredList;
  };

  const noDuplicateOwners = removeDuplicate(owners);

  const HeaderTool = (
    <SuiBox display="flex" alignItems="center" gap={0.5} overflow="auto">
      <SuiTypography sx={{ mr: 0.5, whiteSpace: "noWrap", minWidth: 100 }}>
        Filter by owner:
      </SuiTypography>
      {noDuplicateOwners.map((x) => {
        const storeInfo = x?.stores?.[0] || {};
        return (
          <Tooltip title={storeInfo?.name}>
            <SuiBox
              borderRadius="50%"
              width={avatarSize + 8}
              height={avatarSize + 8}
              minWidth={avatarSize + 8}
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              border={({ borders: { borderWidth }, palette: { info } }) =>
                selectedOwner.id == x?.id ? `${borderWidth[1]} solid ${info.main}` : "none"
              }
              sx={{ cursor: "pointer" }}
            >
              <Avatar
                src={storeInfo?.metadata?.avatar}
                sx={{ width: avatarSize, height: avatarSize, cursor: "pointer" }}
                onClick={() => {
                  handleFilterMoodboard(x);
                }}
              />
            </SuiBox>
          </Tooltip>
        );
      })}
    </SuiBox>
  );

  return (
    <SuiBox>
      <SuiBox sx={{ overflow: "auto" }}>
        {moodboardRows.length > 0 ? (
          <SuiBox>
            <DataTable
              table={{ columns: columns, rows: moodboardRows }}
              entriesPerPage={{ defaultValue: 10 }}
              showTotalEntries={true}
              pagination={true}
              canSearch
              handleRowClick={handleCollectionRowClick}
              HeaderTool={HeaderTool}
              {...tableOptions}
            />{" "}
          </SuiBox>
        ) : (
          <Empty description={false} />
        )}
      </SuiBox>
    </SuiBox>
  );
};

export default SharedMoodboardList;
